import React, { useRef, useEffect, useState, useCallback, useMemo } from "react"
import moment from "moment"
import "moment/locale/fr"
import { getDistance } from "geolib"

import config from "../../config"
import { locales } from "../../utils/locale/MomentLocale"
import { showMixedEventsFlux } from "../../api/eventActions"
import Detail from "../../components/detail/Detail"

import "./Feed.css"
import {cleanText} from "../../utils/functions";

const BASE_URL = config.BASE_URL
const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='500' fill='%231a1a2e'%3E%3Crect width='400' height='500'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23555' font-size='14'%3EImage indisponible%3C/text%3E%3C/svg%3E"

// ─── Helpers ──────────────────────────────────────────────

const buildCoverUrl = (event) => {
    if (event.is_xml === 1 || event.is_xml === "1") {
        return event.imgPathLarge || ""
    }
    if (event.is_firebase === 1 || event.is_firebase === "1") {
        return event.url || ""
    }
    if (event.image_name) {
        return `${BASE_URL}img/events/${event.image_name}?${event.updated_at}`
    }
    return `${BASE_URL}img/events/cover-${event.ev_id}.jpg?${event.updated_at}`
}

const cleanDescription = (html) => {
    if (!html) return ""
    return cleanText(html)
}

const formatDistance = (userCoords, eventLat, eventLng) => {
    if (!userCoords.lat || !userCoords.lng || !eventLat || !eventLng) return null
    const distance = getDistance(
        { latitude: userCoords.lat, longitude: userCoords.lng },
        { latitude: eventLat, longitude: eventLng }
    )
    if (distance > 999) return `${(distance / 1000).toFixed(1)} km`
    return `${distance} m`
}

const formatEventDate = (dtFrom, dtTo) => {
    const from = moment(dtFrom)
    const to = moment(dtTo)
    const now = moment()

    if (now.isBetween(from, to)) {
        return { label: "En cours", accent: true }
    }
    if (from.isSame(now, "day")) {
        return { label: `Aujourd'hui à ${from.format("HH:mm")}`, accent: true }
    }
    if (from.isSame(now.clone().add(1, "day"), "day")) {
        return { label: `Demain à ${from.format("HH:mm")}`, accent: false }
    }
    return { label: from.format("ddd DD MMM · HH:mm") + " -> " + to.format("HH:mm"), accent: false }
}

const formatPrice = (isFree, tarif, tarifPromo) => {
    if (isFree === true || isFree === 1 || isFree === "1") return "Gratuit"
    const t = parseFloat(tarif)
    const p = parseFloat(tarifPromo)
    if (p > 0 && t > 0) return { original: `${t}€`, promo: `${p}€` }
    if (t > 0) return `${t}€`
    return null
}

// ─── Sub-components ───────────────────────────────────────

const EventCardSkeleton = () => (
    <div className="feed-card feed-card--skeleton">
        <div className="feed-card__cover skeleton-pulse" />
        <div className="feed-card__body">
            <div className="skeleton-line skeleton-line--title skeleton-pulse" />
            <div className="skeleton-line skeleton-line--short skeleton-pulse" />
            <div className="skeleton-line skeleton-line--medium skeleton-pulse" />
        </div>
    </div>
)

const JustNowBadge = () => (
    <span className="feed-card__badge">JustNow&nbsp;!</span>
)

const EventCard = React.memo(({ event, coords, index, onOpen }) => {
    const [imgError, setImgError] = useState(false)
    const [imgLoaded, setImgLoaded] = useState(false)

    const coverUrl = useMemo(() => buildCoverUrl(event), [event])
    const distance = useMemo(
        () => formatDistance(coords, event.lat, event.lng),
        [coords, event.lat, event.lng]
    )
    const dateInfo = useMemo(
        () => formatEventDate(event.dt_from, event.dt_to),
        [event.dt_from, event.dt_to]
    )
    const description = useMemo(
        () => cleanDescription(event.details),
        [event.details]
    )
    const price = useMemo(
        () => formatPrice(event.is_free, event.tarif, event.tarif_promo),
        [event.is_free, event.tarif, event.tarif_promo]
    )

    const isLive = moment().isBetween(moment(event.dt_from), moment(event.dt_to))
    const isToday = moment(event.dt_to).isSame(moment(), "day")

    const truncatedDesc = description.length > 180
        ? `${description.substring(0, 180)}…`
        : description

    return (
        <div
            className="feed-card"
            style={{ "--card-index": index, cursor: "pointer" }}
            onClick={() => onOpen(String(event.ev_id), String(event.is_xml ?? "0"))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onOpen(String(event.ev_id), String(event.is_xml ?? "0"))}
        >
            {/* Cover */}
            <div className="feed-card__cover-wrapper">
                <img
                    src={imgError ? PLACEHOLDER_IMAGE : coverUrl}
                    alt={event.ev_name}
                    className={`feed-card__cover ${imgLoaded ? "feed-card__cover--loaded" : ""}`}
                    loading="lazy"
                    onError={() => setImgError(true)}
                    onLoad={() => setImgLoaded(true)}
                />

                <div className="feed-card__overlay" />

                {isLive && (
                    <span className="feed-card__live-indicator">
                        <span className="feed-card__live-dot" />
                        Maintenant
                    </span>
                )}
                {isToday && !isLive && <JustNowBadge />}

                {price && (
                    <span className="feed-card__price">
                        {typeof price === "object" ? (
                            <>
                                <s className="feed-card__price-original">{price.original}</s>
                                {" "}{price.promo}
                            </>
                        ) : (
                            price
                        )}
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="feed-card__body">
                <h3 className="feed-card__title">{event.ev_name}</h3>

                <div className="feed-card__meta">
                    <span className={`feed-card__date ${dateInfo.accent ? "feed-card__date--accent" : ""}`}>
                        <svg className="feed-card__icon" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        {dateInfo.label}
                    </span>

                    {distance && (
                        <span className="feed-card__distance">
                            <svg className="feed-card__icon" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                            {distance}
                        </span>
                    )}
                </div>

                {truncatedDesc && (
                    <p className="feed-card__description">{truncatedDesc}</p>
                )}
            </div>
        </div>
    )
})

EventCard.displayName = "EventCard"

// ─── Detail Modal ─────────────────────────────────────────

const DetailModal = ({ eid, xml, onClose }) => {
    // Fermeture avec Escape
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "Escape") onClose()
        }
        document.addEventListener("keydown", handleKey)
        // Bloquer le scroll du body
        document.body.style.overflow = "hidden"
        return () => {
            document.removeEventListener("keydown", handleKey)
            document.body.style.overflow = ""
        }
    }, [onClose])

    return (
        <div className="feed-modal-overlay" onClick={onClose}>
            <div
                className="feed-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <Detail eid={eid} xml={xml} onClose={onClose} />
            </div>
        </div>
    )
}

// ─── Feed (main component) ───────────────────────────────

export default function Feed() {
    const [events, setEvents] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [coords, setCoords] = useState({ lat: null, lng: null })
    const [selectedEvent, setSelectedEvent] = useState(null) // { eid, xml }

    const pageRef = useRef(1)
    const loadingRef = useRef(false)
    const observerTarget = useRef(null)

    locales("fr")

    // ─── Géolocalisation ─────────────────────────────────

    useEffect(() => {
        const lat = localStorage.getItem("lat")
        const lng = localStorage.getItem("long")
        if (lat && lng) {
            setCoords({ lat, lng })
        }
    }, [])

    // ─── Chargement des événements ───────────────────────

    const loadEvents = useCallback(async () => {
        if (loadingRef.current || !hasMore) return
        loadingRef.current = true

        const isFirstPage = pageRef.current === 1
        if (isFirstPage) setLoading(true)
        else setLoadingMore(true)

        try {
            const response = await showMixedEventsFlux(
                pageRef.current,
                coords.lat,
                coords.lng
            )

            if (response?.data) {
                const newEvents = Array.isArray(response.data)
                    ? response.data
                    : Object.values(response.data)

                if (newEvents.length === 0) {
                    setHasMore(false)
                } else {
                    setEvents((prev) =>
                        isFirstPage ? newEvents : [...prev, ...newEvents]
                    )
                    pageRef.current += 1
                }
            } else {
                setHasMore(false)
            }
        } catch (err) {
            console.error("Erreur chargement feed:", err)
        } finally {
            setLoading(false)
            setLoadingMore(false)
            loadingRef.current = false
        }
    }, [coords, hasMore])

    useEffect(() => {
        if (coords.lat && coords.lng) {
            pageRef.current = 1
            setEvents([])
            setHasMore(true)
            loadEvents()
        }
    }, [coords]) // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Intersection Observer (infinite scroll) ─────────

    useEffect(() => {
        const target = observerTarget.current
        if (!target) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingRef.current && hasMore) {
                    loadEvents()
                }
            },
            { rootMargin: "400px" }
        )

        observer.observe(target)
        return () => observer.disconnect()
    }, [loadEvents, hasMore])

    const validEvents = useMemo(
        () => events.filter((e) => e.ev_id),
        [events]
    )

    const handleOpen = useCallback((eid, xml) => {
        setSelectedEvent({ eid, xml })
    }, [])

    const handleClose = useCallback(() => {
        setSelectedEvent(null)
    }, [])

    // ─── Rendu ───────────────────────────────────────────

    if (!coords.lat || !coords.lng) {
        return (
            <div className="feed-empty">
                <svg className="feed-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <p>Activez la géolocalisation pour découvrir les événements près de vous.</p>
            </div>
        )
    }

    return (
        <>
            <section className={`feed ${selectedEvent ? "feed--blurred" : ""}`}>
                <div className="feed__grid">
                    {loading &&
                        Array.from({ length: 8 }).map((_, i) => (
                            <EventCardSkeleton key={`sk-${i}`} />
                        ))
                    }

                    {!loading &&
                        validEvents.map((event, index) => (
                            <EventCard
                                key={`${event.ev_id}-${event.is_xml}`}
                                event={event}
                                coords={coords}
                                index={index}
                                onOpen={handleOpen}
                            />
                        ))
                    }
                </div>

                <div ref={observerTarget} className="feed__sentinel" />

                {loadingMore && (
                    <div className="feed__loader">
                        <div className="feed__spinner" />
                        <span>Chargement…</span>
                    </div>
                )}

                {!hasMore && validEvents.length > 0 && (
                    <p className="feed__end-message">Vous avez tout vu 🎉</p>
                )}

                {!loading && validEvents.length === 0 && (
                    <div className="feed-empty">
                        <p>Aucun événement trouvé à proximité pour le moment.</p>
                    </div>
                )}
            </section>

            {selectedEvent && (
                <DetailModal
                    eid={selectedEvent.eid}
                    xml={selectedEvent.xml}
                    onClose={handleClose}
                />
            )}
        </>
    )
}
